import { Suspense, useEffect } from "react";
import { Await, useAsyncValue, Outlet, useNavigate } from "react-router";
import { stem } from "porter2";
import { useAccountStore } from "./stores/accountStore";
import { useSettingsStore } from "./stores/settingsStore";
import { useUIStore } from "./stores/uiStore";
import { ios, webkit, href } from "./util";
import { newAccount } from "./components/auth/Login";
import { ConfirmDialog } from "./components/ConfirmDialog";
import { CallWindow } from "./components/call/CallWindow";
import { createPersistenceLayer } from "./lib/xmpp/factories";
import "./app.css";

// Bootstrap the persistence layer for the "snikket" namespace.
// Safari/iOS get SQLite (OPFS-backed); everyone else gets IDB. See
// REQ-CN-001.B.5a in traceability-matrix.md for the factory contract.
const { persistence, idbp } = await createPersistenceLayer(
	"snikket",
	stem,
	webkit !== null || ios !== null,
);

type InitialData = {
	initialAccounts: Record<string, ReturnType<typeof newAccount>>;
	initialSelectedAccount: string;
	initialSettings: {
		enterIsSend: boolean;
		richTextToolbar: boolean;
		audibleNotifications: boolean;
		messageRequests: string;
	};
};

const AppInner = () => {
	const { initialSelectedAccount } = useAsyncValue() as InitialData;

	const { accounts } = useAccountStore();
	const navigate = useNavigate();

	useEffect(() => {
		window.accounts = Object.freeze(accounts);
		const { setActiveCall } = useUIStore.getState();
		const tokens = Object.fromEntries(
			Object.keys(accounts).map((accountId) => {
				const client = accounts[accountId].client;
				const { setStatus } = useAccountStore.getState();
				return [
					accountId,
					[
						client.addStatusOnlineListener(() => setStatus(accountId, "online")),
						client.addStatusOfflineListener(() => setStatus(accountId, "connecting")),
						client.addConnectionFailedListener(() => setStatus(accountId, "error")),
						client.addPasswordNeededListener(() => setStatus(accountId, "auth")),
						// Incoming call
						client.addCallRingListener?.((session: any) => {
							setActiveCall({ session, isIncoming: true, chatId: session.get_chatId?.() ?? "", accountId, hasVideo: false, screenSharing: false });
						}),
					].filter(Boolean),
				];
			}),
		);
		return () => {
			for (const [accountId, ts] of Object.entries(tokens)) {
				(ts as any[]).forEach((t: any) => accounts[accountId].client.removeEventListener(t));
			}
		};
	}, [accounts]);

	useEffect(() => {
		if (!navigator.serviceWorker) return;
		const handler = (event: MessageEvent) => {
			const accountId = event.data?.data?.accountId;
			const chatId = event.data?.data?.chatId;
			if (event.data.event === "notificationclick") {
				navigate(
					href("/accounts/:accountId/chats/:chatId", { accountId, chatId }) +
						`#message:${event.data.data.messageId}`,
				);
			}
		};
		navigator.serviceWorker.addEventListener("message", handler);
		return () => navigator.serviceWorker.removeEventListener("message", handler);
	}, [accounts, navigate]);

	return (
		<>
			<CallWindow accounts={accounts} />
			<Outlet context={{ accounts, persistence, idbp, initialSelectedAccount }} />
		</>
	);
};

const initialP = (async () => {
	const p = persistence as any;
	const accountIds = await p.listAccounts();
	const initialSelectedAccount = (await p.get("ui#selectedAccount")) || accountIds[0];
	const initialAccounts = Object.fromEntries(
		accountIds.map((id: string) => [id, newAccount(id, idbp as any, persistence)]),
	);
	const initialSettings = {
		enterIsSend: "" + (await p.get("config#enterIsSend")) !== "false",
		richTextToolbar: "" + (await p.get("config#richTextToolbar")) === "true",
		audibleNotifications: "" + (await p.get("config#audibleNotifications")) === "true",
		messageRequests: (await p.get("config#messageRequests")) ?? "spam",
	};
	// Initialize Zustand store eagerly so Home doesn't flash to /login before useEffect runs
	useAccountStore.getState().setPersistence(persistence, idbp as any);
	useAccountStore.getState().setAccounts(initialAccounts);
	if (initialSelectedAccount) useAccountStore.getState().setActiveAccount(initialSelectedAccount);
	useSettingsStore.getState().setSettings(initialSettings);
	return { initialAccounts, initialSelectedAccount, initialSettings };
})();

const LoadingSpinner = () => (
	<div className="flex items-center justify-center h-full min-h-screen">
		<div className="w-8 h-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
	</div>
);

export { persistence, idbp };

export const App = () => {
	return (
		<>
			<ConfirmDialog />
			<Suspense fallback={<LoadingSpinner />}>
				<Await resolve={initialP}>
					<AppInner />
				</Await>
			</Suspense>
		</>
	);
};
